import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router';
import { CheckCircle, XCircle, Eye, EyeOff, Lock01 } from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Button } from '@/components/base/buttons/button';
import { verifyResetToken, resetPassword } from '@/data/api/auth';
import { useTranslation } from 'react-i18next';
import { VersionFooter } from '@/components/base/version-footer/version-footer';

const AuthLogo = () => <img src="/images/logo-full.svg" alt="Eve Mind" className="h-14 w-auto sm:h-16" />;

export const NewPasswordPage = () => {
    const { t } = useTranslation(['pages:auth', 'common']);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [email, setEmail] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [showPasswords, setShowPasswords] = useState({
        password: false,
        confirm: false,
    });
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError(t('newPassword.noToken'));
                setIsLoading(false);
                return;
            }

            try {
                const data = await verifyResetToken(token);
                
                if (!data.valid) {
                    setError(t('newPassword.invalidToken'));
                    setIsLoading(false);
                    return;
                }
                
                setIsValid(true);
                setEmail(data.email || null);
                setError(null);
            } catch (err: any) {
                setError(err.message || t('newPassword.invalidToken'));
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();
    }, [token, t]);

    const validatePassword = (): boolean => {
        if (password.length < 8) {
            setPasswordError(t('newPassword.passwordTooShort'));
            return false;
        }
        if (password !== passwordConfirm) {
            setPasswordError(t('newPassword.passwordsDoNotMatch'));
            return false;
        }
        setPasswordError(null);
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validatePassword() || !token) {
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            await resetPassword(token, password, passwordConfirm);
            setSuccess(true);
            setTimeout(() => {
                navigate('/auth/login', { 
                    state: { message: t('newPassword.successMessage') }
                });
            }, 2000);
        } catch (err: any) {
            setError(err.message || t('newPassword.resetError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="mb-8 flex justify-center">
                        <AuthLogo />
                    </div>
                    <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary text-center">
                        <p className="text-tertiary">{t('newPassword.verifying')}</p>
                    </div>
                </div>
                <VersionFooter />
            </div>
        );
    }

    if (error && !isValid) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="mb-8 flex justify-center">
                        <AuthLogo />
                    </div>
                    <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary text-center">
                        <div className="flex justify-center mb-4">
                            <XCircle className="size-12 text-error-600" />
                        </div>
                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('newPassword.errorTitle')}
                        </h1>
                        <p className="text-sm text-tertiary mb-6">
                            {error}
                        </p>
                        <Link
                            to="/auth/login"
                            className="inline-flex items-center justify-center rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover w-full"
                        >
                            {t('newPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
                <VersionFooter />
            </div>
        );
    }

    if (success) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="mb-8 flex justify-center">
                        <AuthLogo />
                    </div>
                    <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary text-center">
                        <div className="flex justify-center mb-4">
                            <CheckCircle className="size-12 text-success-primary" />
                        </div>
                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('newPassword.successTitle')}
                        </h1>
                        <p className="text-sm text-tertiary mb-6">
                            {t('newPassword.successMessage')}
                        </p>
                        <p className="text-sm text-tertiary">
                            {t('newPassword.redirecting')}
                        </p>
                    </div>
                </div>
                <VersionFooter />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-secondary px-4 py-12">
            <div className="w-full max-w-md">
                <div className="mb-8 flex justify-center">
                    <AuthLogo />
                </div>

                <div className="rounded-xl bg-primary p-8 shadow-lg ring-1 ring-secondary mb-4">
                    <div className="mb-6 text-center">
                        <h1 className="text-2xl font-semibold text-primary mb-2">
                            {t('newPassword.title')}
                        </h1>
                        <p className="text-sm text-tertiary">
                            {t('newPassword.subtitle')}
                            {email && (
                                <span className="block mt-1 font-medium">{email}</span>
                            )}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="relative">
                            <Input
                                label={t('newPassword.passwordLabel')}
                                placeholder={t('newPassword.passwordPlaceholder')}
                                value={password}
                                onChange={setPassword}
                                type={showPasswords.password ? 'text' : 'password'}
                                icon={Lock01}
                                size="md"
                                isRequired
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({ ...showPasswords, password: !showPasswords.password })}
                                className="absolute right-3 top-8 text-tertiary hover:text-primary"
                            >
                                {showPasswords.password ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                            </button>
                        </div>

                        <div className="relative">
                            <Input
                                label={t('newPassword.passwordConfirmLabel')}
                                placeholder={t('newPassword.passwordConfirmPlaceholder')}
                                value={passwordConfirm}
                                onChange={setPasswordConfirm}
                                type={showPasswords.confirm ? 'text' : 'password'}
                                icon={Lock01}
                                size="md"
                                isRequired
                                isInvalid={password !== passwordConfirm && passwordConfirm.length > 0}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                className="absolute right-3 top-8 text-tertiary hover:text-primary"
                            >
                                {showPasswords.confirm ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                            </button>
                        </div>

                        {passwordError && (
                            <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 flex items-center gap-2">
                                <XCircle className="size-5" />
                                {passwordError}
                            </div>
                        )}

                        {password !== passwordConfirm && passwordConfirm.length > 0 && (
                            <p className="text-sm text-error-600">{t('newPassword.passwordsDoNotMatch')}</p>
                        )}

                        {error && (
                            <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 flex items-center gap-2">
                                <XCircle className="size-5" />
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            size="lg"
                            color="primary"
                            isLoading={isSubmitting}
                            className="w-full"
                        >
                            {t('newPassword.submitButton')}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link
                            to="/auth/login"
                            className="inline-flex items-center text-sm text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            {t('newPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
                <VersionFooter />
            </div>
        </div>
    );
};

